"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";

import { BannerItem, resolveBannerLinkHref } from "@/utils/library/banner";
import {
  filterAckedBannerItems,
  loadBannerItems,
} from "@/utils/library/bannerFetch";
import Button from "@/components/library/Button";
import FancyLoader from "@/components/common/FancyLoader";
import { SupportedLanguages } from "@/utils/library/language";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastViewport,
} from "@/components/ui/toast";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

type GenericModalProps = {
  open: boolean;
  message: string;
  description?: string;
};

export type ConfirmSettings = {
  message: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Styles the confirm action as destructive delete. */
  isDelete?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
};

type MainAlertProps = {
  status: "success" | "error" | "warning" | "info";
  message: string;
  secondsToClose?: 4 | 10 | 30;
  btnLabel?: string;
  btnAction?: () => void;
};

type AlertProps = {
  setAlert: (settings: MainAlertProps) => void;
  dismissAlert: () => void;
  setConfirm: (settings: ConfirmSettings | null) => void;
  showToast: boolean;
  toastSettings: MainAlertProps;
  isProcessing: GenericModalProps;
  setIsProcessing: (
    isSaving: boolean,
    message?: string,
    description?: string,
  ) => void;
  bannerItems: BannerItem[];
  removeBannerItem: (id: string) => void;
};

const getIcon = (
  status: "success" | "error" | "warning" | "info",
):
  | "check-circle"
  | "circle-exclamation"
  | "triangle-exclamation"
  | "circle-info" => {
  switch (status) {
    case "success":
      return "check-circle";
    case "error":
      return "circle-exclamation";
    case "warning":
      return "triangle-exclamation";
    case "info":
      return "circle-info";
  }
};

export const AlertContext = createContext<AlertProps>({
  setAlert: () => {},
  dismissAlert: () => {},
  setConfirm: () => {},
  showToast: false,
  toastSettings: {
    status: "success",
    message: "",
  },
  isProcessing: {
    open: false,
    message: "",
  },
  setIsProcessing: () => {},
  bannerItems: [],
  removeBannerItem: () => {},
});

export const useAlert = () => useContext(AlertContext)!;

type AlertInternalState = AlertProps & {
  confirmSettings: ConfirmSettings | null;
  confirmBusy: boolean;
  setConfirmBusy: (busy: boolean) => void;
};

const useAlertFunction = (): AlertInternalState => {
  const { t: ct } = useTranslation("common");

  //Private State
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  //Public State
  const [showToast, setShowToast] = useState(false);
  const [toastSettings, setToastSettings] = useState<MainAlertProps>({
    status: "success",
    message: "",
  });
  const [isProcessing, setIsProcessing] = useState<GenericModalProps>({
    open: false,
    message: "",
    description: "",
  });
  const [confirmSettings, setConfirmSettings] =
    useState<ConfirmSettings | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [bannerItems, setBannerItems] = useState<BannerItem[]>([]);

  useEffect(() => {
    let ignore = false;

    void loadBannerItems()
      .then((items) => {
        if (ignore) return;
        const ackedIdsString = window.localStorage.getItem("GAIL_banner_ids");
        setBannerItems(filterAckedBannerItems(items, ackedIdsString));
      })
      .catch(() => {
        if (ignore) return;
        setBannerItems([]);
      });

    return () => {
      ignore = true;
    };
  }, []);

  //Public Methods
  const setAlert = (settings: MainAlertProps) => {
    setToastSettings(settings);
    setShowToast(true);

    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    const secondsToClose = settings.secondsToClose || 0;

    if (secondsToClose > 0) {
      const timeout = setTimeout(() => {
        setShowToast(false);
      }, secondsToClose * 1000);

      setTimeoutId(timeout);
    }
  };

  const dismissAlert = () => {
    setShowToast(false);
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  };

  const setConfirm = (settings: ConfirmSettings | null) => {
    setConfirmBusy(false);
    setConfirmSettings(settings);
  };

  const handleRemoveBannerItem = (id: string) => {
    const ackedIds = window.localStorage.getItem("GAIL_banner_ids");
    if (ackedIds) {
      window.localStorage.setItem("GAIL_banner_ids", `${ackedIds},${id}`);
    } else {
      window.localStorage.setItem("GAIL_banner_ids", id);
    }

    setBannerItems(bannerItems.filter((item) => item.id !== id));
  };

  return {
    setAlert,
    dismissAlert,
    setConfirm,
    showToast,
    toastSettings,
    isProcessing,
    setIsProcessing: (isLoading, message, description) => {
      setIsProcessing({
        open: isLoading,
        message: message || ct("loadingDots"),
        description: description,
      });
    },
    bannerItems,
    removeBannerItem: handleRemoveBannerItem,
    confirmSettings,
    confirmBusy,
    setConfirmBusy,
  };
};

export const AlertContextProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const alert = useAlertFunction();

  const {
    dismissAlert,
    setConfirm,
    showToast,
    toastSettings,
    isProcessing,
    bannerItems,
    removeBannerItem,
    confirmSettings,
    confirmBusy,
    setConfirmBusy,
  } = alert;

  const icon = getIcon(toastSettings.status);

  const contextValue: AlertProps = {
    setAlert: alert.setAlert,
    dismissAlert: alert.dismissAlert,
    setConfirm: alert.setConfirm,
    showToast: alert.showToast,
    toastSettings: alert.toastSettings,
    isProcessing: alert.isProcessing,
    setIsProcessing: alert.setIsProcessing,
    bannerItems: alert.bannerItems,
    removeBannerItem: alert.removeBannerItem,
  };

  async function handleConfirm() {
    if (!confirmSettings || confirmBusy) return;
    setConfirmBusy(true);
    try {
      await confirmSettings.onConfirm();
      setConfirm(null);
    } catch {
      setConfirmBusy(false);
    }
  }

  function handleCancelConfirm() {
    if (confirmBusy) return;
    confirmSettings?.onCancel?.();
    setConfirm(null);
  }

  return (
    <AlertContext.Provider value={contextValue}>
      {bannerItems.length > 0 ? (
        <Alert
          key={bannerItems[0].id}
          variant={bannerItems[0].status}
          className={clsx(
            "font-sans",
            "p-0",
            "rounded-none",
            "border-0",
            "relative",
            "z-40",
          )}
        >
          <AlertDescription
            className={clsx(
              "flex",
              "w-full",
              "justify-between",
              "p-4 md:py-2",
              "w-auto",
              "text-current",
            )}
          >
            <div className={clsx("md:mx-auto", "flex", "items-center")}>
              <div
                className={clsx(
                  "flex",
                  "items-center",
                  "text-sm",
                  "font-normal",
                )}
              >
                <div className="flex md:flex-row flex-col md:items-center">
                  <div>
                    <Icon icon={bannerItems[0].icon} className={clsx("mr-3")} />
                    {Object.keys(bannerItems[0].description).includes(
                      i18n.language as SupportedLanguages,
                    )
                      ? bannerItems[0].description[
                          i18n.language as SupportedLanguages
                        ]
                      : bannerItems[0].description[SupportedLanguages.English]}
                  </div>
                  {bannerItems[0].links && bannerItems[0].links.length > 0 ? (
                    <div
                      className={clsx(
                        "flex",
                        "flex-wrap",
                        "items-center",
                        "gap-x-3",
                        "gap-y-1",
                      )}
                    >
                      {bannerItems[0].links.map((link) => {
                        const href = resolveBannerLinkHref(link);
                        const label = Object.keys(link.text).includes(
                          i18n.language as SupportedLanguages,
                        )
                          ? link.text[i18n.language as SupportedLanguages]
                          : link.text[SupportedLanguages.English];
                        if (!label) {
                          return null;
                        }
                        const linkKey = `${bannerItems[0].id}-${link.type}-${link.value}`;
                        return (
                          <Button
                            key={linkKey}
                            id={linkKey}
                            href={href}
                            className={clsx(
                              "inline",
                              "font-medium",
                              "text-button_primary",
                              "bg-transparent!",
                              "underline",
                              "decoration-solid",
                              "underline-offset-2",
                              "cursor-pointer",
                              "hover:no-underline",
                            )}
                            textAlign="left"
                            target={
                              href.startsWith("http") ? "_blank" : "_self"
                            }
                          >
                            {label}
                          </Button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            {!bannerItems[0].isPersistent && (
              <button
                type="button"
                className={clsx("border-0", "bg-transparent")}
                onClick={() => removeBannerItem(bannerItems[0].id)}
              >
                <Icon icon="close" />
              </button>
            )}
          </AlertDescription>
        </Alert>
      ) : null}
      {children}
      <ToastProvider
        duration={
          toastSettings.secondsToClose
            ? toastSettings.secondsToClose * 1000
            : Number.POSITIVE_INFINITY
        }
      >
        <Toast
          open={showToast}
          onOpenChange={(open) => {
            if (!open) {
              dismissAlert();
            }
          }}
          className={clsx({
            "border-green-500": toastSettings.status === "success",
            "border-red-500": toastSettings.status === "error",
            "border-yellow-500": toastSettings.status === "warning",
            "border-blue-500": toastSettings.status === "info",
          })}
        >
          <span
            className={clsx({ hidden: toastSettings.status !== "success" })}
          >
            <Icon
              icon={icon}
              className={clsx("text-green-500")}
              size={ICON_SIZES.XL}
            />
          </span>
          <span className={clsx({ hidden: toastSettings.status !== "error" })}>
            <Icon
              icon={icon}
              className={clsx("text-red-500")}
              size={ICON_SIZES.XL}
            />
          </span>
          <span
            className={clsx({ hidden: toastSettings.status !== "warning" })}
          >
            <Icon
              icon={icon}
              className={clsx("text-yellow-500")}
              size={ICON_SIZES.XL}
            />
          </span>
          <span className={clsx({ hidden: toastSettings.status !== "info" })}>
            <Icon
              icon={icon}
              className={clsx("text-blue-500")}
              size={ICON_SIZES.XL}
            />
          </span>
          <ToastDescription className="gap-2 grid pr-2 pl-4 w-full">
            {toastSettings.message
              .split("\n")
              .map((line: string, i: number) => (
                <div key={i}>{line}</div>
              ))}
          </ToastDescription>
          {toastSettings.btnLabel && toastSettings.btnAction && (
            <Button
              className={clsx("mx-2", "float-right", {
                "text-green-500": toastSettings.status === "success",
                "text-red-500": toastSettings.status === "error",
                "text-yellow-500": toastSettings.status === "warning",
                "text-blue-500": toastSettings.status === "info",
              })}
              type="secondary"
              onClick={toastSettings.btnAction}
            >
              {toastSettings.btnLabel}
            </Button>
          )}
          <ToastClose
            className={clsx("bg-gray-100")}
            onClick={() => dismissAlert()}
          >
            <Icon icon="close" />
          </ToastClose>
        </Toast>
        <ToastViewport />
      </ToastProvider>
      <Dialog open={isProcessing.open}>
        <DialogContent>
          <DialogHeader>
            <h1
              className={clsx("text-center", "text-xl", {
                "mb-4": !!isProcessing.description,
              })}
            >
              {isProcessing.message}
            </h1>
            {isProcessing.description && (
              <DialogDescription className={clsx("text-sm text-center")}>
                {isProcessing.description}
              </DialogDescription>
            )}
          </DialogHeader>
          <FancyLoader />
        </DialogContent>
      </Dialog>
      <Dialog
        open={confirmSettings != null}
        onOpenChange={(open) => {
          if (!open) handleCancelConfirm();
        }}
        blurBackdrop
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>
              {confirmSettings?.message ?? "Are you sure?"}
            </DialogTitle>
            {confirmSettings?.description ? (
              <DialogDescription>
                {confirmSettings.description}
              </DialogDescription>
            ) : null}
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="secondary"
              disabled={confirmBusy}
              onClick={handleCancelConfirm}
            >
              {confirmSettings?.cancelLabel ?? "Cancel"}
            </Button>
            <Button
              type="primary"
              disabled={confirmBusy}
              className={
                confirmSettings?.isDelete
                  ? "bg-red-600 hover:bg-red-600/90"
                  : undefined
              }
              onClick={() => void handleConfirm()}
            >
              {confirmBusy
                ? "Working…"
                : (confirmSettings?.confirmLabel ??
                  (confirmSettings?.isDelete ? "Delete" : "Confirm"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AlertContext.Provider>
  );
};
